import React, { useEffect, useRef, useState } from "react";
import {
  Box, Paper, Typography, Button, Chip, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Card, CardContent, Grid, Alert
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  OUTCOMES, CALL_STATES, CAMPAIGN_STATUS, SIP_STATES, PURPOSES, formatNumber, dhakaTime, reasonText
} from "./useAICalling";
import "./AICalling.css";

/* ------------------------------------------------------------------ navigation */

export const AI_CALLING_PAGES = [
  { to: "/ai-calling", label: "Overview", exact: true },
  { to: "/ai-calling/live", label: "Live calls" },
  { to: "/ai-calling/campaigns", label: "Campaigns" },
  { to: "/ai-calling/calls", label: "Call history" },
  { to: "/ai-calling/reports", label: "Reports" },
  { to: "/ai-calling/ticket-agent", label: "Ticket agent" },
  { to: "/ai-calling/dnc", label: "Do not call" },
  { to: "/ai-calling/settings", label: "Settings" },
  { to: "/ai-calling/sip", label: "SIP line" },
];

const currentPage = (pathname) => {
  const path = pathname.replace(/\/+$/, "") || "/";
  const match = AI_CALLING_PAGES.filter((p) => (p.exact ? path === p.to : path === p.to || path.startsWith(`${p.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match ? match.to : false;
};

/** Tabs linking the AI Calling pages (scrolls sideways on phones). */
export const AICallingNav = () => {
  const { pathname } = useLocation();
  return (
    <Box className="aic-nav">
      <Tabs value={currentPage(pathname)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
        {AI_CALLING_PAGES.map((p) => (
          <Tab key={p.to} value={p.to} label={p.label} component={RouterLink} to={p.to} sx={{ textTransform: "none", minWidth: 0 }} />
        ))}
      </Tabs>
    </Box>
  );
};

/**
 * Page frame: container, toasts, title row (optional back button and actions) and the section tabs.
 * back: { to, label } or { onClick, label }
 */
export const AICallingPage = ({ title, subtitle, back, actions, children }) => {
  const navigate = useNavigate();
  return (
    <Box className="aic-container">
      <ToastContainer position="top-right" autoClose={4000} newestOnTop />
      <Box className="aic-header">
        <Box sx={{ minWidth: 0 }}>
          {back && (
            <Button
              size="small"
              startIcon={<ArrowBack />}
              onClick={back.onClick || (() => navigate(back.to))}
              sx={{ mb: 0.5, ml: -0.5, textTransform: "none" }}
            >
              {back.label || "Back"}
            </Button>
          )}
          <h1 className="aic-title">{title}</h1>
          {subtitle && <p className="aic-subtitle">{subtitle}</p>}
        </Box>
        {actions && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>{actions}</Box>}
      </Box>
      <AICallingNav />
      {children}
    </Box>
  );
};

/* ------------------------------------------------------------------ layout pieces */

/** A titled block (Paper) with an optional action on the right. */
export const SectionCard = ({ title, subtitle, action, children, sx }) => (
  <Paper className="aic-section" sx={sx}>
    {(title || action) && (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 0 }}>
          {title && <Typography variant="h6">{title}</Typography>}
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Paper>
);

export const Loading = ({ text = "Loading..." }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
    <CircularProgress />
    <Typography variant="body2" sx={{ mt: 2 }}>{text}</Typography>
  </Box>
);

export const ErrorState = ({ error, onRetry }) => (
  <Alert severity="error" action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}>
    {error || "Something went wrong"}
  </Alert>
);

/** A number tile (same look as CampaignResults). */
export const Metric = ({ label, value, sub, color, xs = 6, sm, md = 3 }) => (
  <Grid item xs={xs} sm={sm} md={md}>
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ color, fontWeight: 600, wordBreak: "break-word" }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary" component="div">{sub}</Typography>}
      </CardContent>
    </Card>
  </Grid>
);

/** One step of a funnel row. value: number (formatted here) or a ready string. */
export const Step = ({ label, value, sub, color }) => (
  <Box sx={{ flex: "1 1 120px", textAlign: "center", p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1, minWidth: 110 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ color, wordBreak: "break-word" }}>{typeof value === "number" ? formatNumber(value) : value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary" component="div">{sub}</Typography>}
  </Box>
);

/** Label above a value (summaries). */
export const KeyValue = ({ label, children, xs = 12, sm = 6, md = 4 }) => (
  <Grid item xs={xs} sm={sm} md={md}>
    <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
    <Box sx={{ typography: "body2", wordBreak: "break-word" }}>{children === undefined || children === null || children === "" ? "—" : children}</Box>
  </Grid>
);

/* ------------------------------------------------------------------ chips */

export const OutcomeChip = ({ outcome, size = "small", title }) => {
  if (!outcome) return <Typography variant="body2" color="text.secondary" component="span">—</Typography>;
  const o = OUTCOMES[outcome] || CALL_STATES[outcome] || { label: reasonText(outcome) || outcome, color: "default" };
  return <Chip size={size} label={o.label} color={o.color} title={title} />;
};

export const CallStateChip = ({ state, size = "small" }) => {
  const s = CALL_STATES[state] || { label: reasonText(state) || "—", color: "default" };
  return <Chip size={size} label={s.label} color={s.color} variant={state === "ended" ? "outlined" : "filled"} />;
};

export const CampaignStatusChip = ({ status, size = "small" }) => {
  const s = CAMPAIGN_STATUS[status] || { label: reasonText(status) || "—", color: "default" };
  return <Chip size={size} label={s.label} color={s.color} />;
};

export const SipStateChip = ({ state, size = "small" }) => {
  const s = SIP_STATES[state] || { label: state ? reasonText(state) : "Unknown", color: "default" };
  return <Chip size={size} label={s.label} color={s.color} />;
};

export const PurposeChip = ({ purpose, size = "small" }) =>
  purpose ? <Chip size={size} variant="outlined" label={PURPOSES[purpose] || reasonText(purpose)} /> : null;

/* ------------------------------------------------------------------ dialogs */

/**
 * Confirm dialog. onConfirm may be async; return false to keep the dialog open.
 * Buttons are disabled while it runs.
 */
export const ConfirmDialog = ({
  open, title, children, confirmText = "Confirm", confirmColor = "primary", onConfirm, onClose, disabled = false
}) => {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    let keepOpen = false;
    try {
      keepOpen = (await onConfirm()) === false;
    } finally {
      setBusy(false);
    }
    if (!keepOpen) onClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{typeof children === "string" ? <Typography>{children}</Typography> : children}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={handleConfirm}
          disabled={busy || disabled}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ transcript */

const ROLE_LABEL = { agent: "Agent", member: "Member", system: "System" };

/**
 * Chat bubbles: agent on the left, member on the right, system notes in the middle.
 * autoScroll keeps the view at the bottom while new lines arrive (unless the user scrolled up).
 */
export const TranscriptView = ({ lines = [], maxHeight = 420, autoScroll = false, emptyText = "No transcript." }) => {
  const boxRef = useRef(null);
  const stickRef = useRef(true);

  const onScroll = () => {
    const el = boxRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  useEffect(() => {
    const el = boxRef.current;
    if (autoScroll && el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [lines, autoScroll]);

  if (!lines.length) {
    return <Typography variant="body2" color="text.secondary">{emptyText}</Typography>;
  }

  return (
    <Box ref={boxRef} className="aic-transcript" sx={{ maxHeight }} onScroll={onScroll}>
      {lines.map((line, i) => {
        const role = ROLE_LABEL[line.role] ? line.role : "system";
        return (
          <Box key={`${line.seq ?? "x"}-${i}`} className={`aic-bubble aic-bubble-${role}`} lang="bn">
            {line.text}
            {role !== "system" && (
              <span className="aic-bubble-meta">
                {ROLE_LABEL[role]}{line.at ? ` · ${dhakaTime(line.at)}` : ""}
              </span>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
